'use client';

import { useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Wallet,
  User,
  PenSquare,
  MessageSquare,
  ArrowRight,
  Shield,
  Zap,
  CheckCircle,
  Globe,
} from 'lucide-react';
import ConnectButtonAuth from '../components/auth/connect-button-auth';
import { Navbar } from '../components/layout/navbar';
import { Footer } from '../components/layout/footer';
import { usePassportSSO, useAuthStore, selectIsAuthenticated } from '../hooks';

export default function SignUpPage() {
  const navigate = useNavigate();
  const { redirectToLogin, isCheckingSession } = usePassportSSO();
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const storeUser = useAuthStore((state) => state.user);

  // Redirect if already authenticated
  useEffect(() => {
    if (isCheckingSession) return;

    if (isAuthenticated && storeUser?.passportId) {
      // If user is not verified, redirect to complete registration
      if (!storeUser.isVerified) {
        navigate('/profile/complete-registration', { replace: true });
        return;
      }
      // If verified, redirect to profile
      const profileId = storeUser.passportId || storeUser.oid;
      navigate(`/profile/${profileId}`, { replace: true });
    }
  }, [navigate, isAuthenticated, storeUser?.passportId, storeUser?.isVerified, isCheckingSession]);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Gradient Background */}
      <div className="fixed inset-0 z-0 pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-full bg-violet-600/20 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-full bg-cyan-600/20 blur-[120px]" />
      </div>

      {/* Navigation */}
      <Navbar />

      {/* Hero Section */}
      <section className="py-12 lg:py-24 relative overflow-hidden z-10">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-900/20 via-black to-pink-900/20 z-0 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <h1 className="text-4xl lg:text-5xl font-bold tracking-tight mb-4">
                  Join the{' '}
                  <span className="bg-gradient-to-r from-purple-400 to-pink-400 text-transparent bg-clip-text">
                    decentralized
                  </span>{' '}
                  conversation
                </h1>
                <p className="text-xl text-zinc-400 max-w-lg">
                  Connect your wallet to access a world of blockchain-powered
                  discussions, articles, and community.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button
                  onClick={redirectToLogin}
                  disabled={isCheckingSession}
                  className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold px-6 py-3 rounded-lg"
                >
                  <Shield className="mr-2 h-5 w-5" />
                  Continue with Passport
                </Button>
                <Link
                  to="/learn-more"
                  className="inline-flex items-center justify-center px-5 py-2.5 border border-white/10 rounded-md text-sm font-medium hover:bg-white/5 text-white transition-colors"
                >
                  Learn how it works
                </Link>
              </div>

              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <CheckCircle className="h-4 w-4 text-green-500" />
                <span>No email or password required</span>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -top-16 -right-16 w-64 h-64 bg-purple-600/20 rounded-full opacity-50 blur-3xl"></div>
              <div className="absolute -bottom-8 -left-8 w-48 h-48 bg-pink-600/20 rounded-full opacity-50 blur-3xl"></div>

              <Card className="border border-white/10 bg-black/40 backdrop-blur-xl shadow-2xl relative z-10 overflow-hidden">
                <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-500/10 to-pink-500/10 rounded-full -translate-y-1/2 translate-x-1/2"></div>
                <CardHeader className="pb-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-r from-purple-600 to-pink-600 mb-4">
                    <Shield className="h-6 w-6 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white">
                    Get Started in Seconds
                  </CardTitle>
                  <CardDescription className="text-base text-zinc-400">
                    Sign in with Google, Twitter, Discord, or your wallet - all in one place!
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500/20 text-purple-400 text-sm font-medium border border-purple-500/30">
                        1
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          Choose Your Sign-In Method
                        </p>
                        <p className="text-sm text-zinc-500">
                          Use Google, Twitter, Discord, or connect your wallet
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500/20 text-purple-400 text-sm font-medium border border-purple-500/30">
                        2
                      </div>
                      <div>
                        <p className="font-medium text-white">
                          Complete Your Profile
                        </p>
                        <p className="text-sm text-zinc-500">
                          Add a username and bio to unlock full features
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3">
                      <div className="flex h-6 w-6 items-center justify-center rounded-full bg-purple-500/20 text-purple-400 text-sm font-medium border border-purple-500/30">
                        3
                      </div>
                      <div>
                        <p className="font-medium text-white">Start Creating</p>
                        <p className="text-sm text-zinc-500">
                          Write articles and join forum discussions
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Passport Sign In Button */}
                  <div className="pt-4">
                    <Button
                      onClick={redirectToLogin}
                      disabled={isCheckingSession}
                      className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 text-white font-semibold py-3 rounded-lg"
                    >
                      <Shield className="mr-2 h-5 w-5" />
                      Sign Up with Passport
                    </Button>
                  </div>

                  <div className="flex items-center gap-4 text-zinc-600 text-xs">
                    <div className="h-px bg-white/10 flex-1" />
                    <span>or connect wallet directly</span>
                    <div className="h-px bg-white/10 flex-1" />
                  </div>

                  <div className="bg-black rounded-lg p-1">
                    <ConnectButtonAuth />
                  </div>
                </CardContent>
                <CardFooter className="bg-white/5 px-6 py-4 border-t border-white/5">
                  <div className="text-sm text-zinc-500">
                    <p>
                      Supported: Google, Twitter, Discord, MetaMask, WalletConnect & more
                    </p>
                  </div>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 bg-black/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4 text-white">
              Why Join Talktrail?
            </h2>
            <p className="text-lg text-zinc-400 max-w-2xl mx-auto">
              Our platform combines the best of blockchain technology with
              community-driven content
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border border-white/10 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-full bg-purple-500/10 flex items-center justify-center mb-4 border border-purple-500/20">
                  <PenSquare className="h-6 w-6 text-purple-400" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">
                  Write Articles
                </h3>
                <p className="text-zinc-400">
                  Share your knowledge and insights with our community through
                  well-crafted articles that can earn you recognition and
                  rewards.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-white/10 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-full bg-pink-500/10 flex items-center justify-center mb-4 border border-pink-500/20">
                  <MessageSquare className="h-6 w-6 text-pink-400" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">
                  Join Discussions
                </h3>
                <p className="text-zinc-400">
                  Participate in forum discussions and connect with like-minded
                  individuals who share your interests and passions.
                </p>
              </CardContent>
            </Card>

            <Card className="border border-white/10 bg-zinc-900/50 hover:bg-zinc-900/80 transition-colors">
              <CardContent className="p-6">
                <div className="h-12 w-12 rounded-full bg-blue-500/10 flex items-center justify-center mb-4 border border-blue-500/20">
                  <Globe className="h-6 w-6 text-blue-400" />
                </div>
                <h3 className="text-xl font-bold mb-2 text-white">
                  Global Community
                </h3>
                <p className="text-zinc-400">
                  Connect with creators, thinkers, and innovators from around
                  the world in a truly decentralized environment.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Registration Requirements */}
      <section className="py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-900/10 to-orange-900/10 pointer-events-none"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-center gap-3 mb-6">
              <Shield className="h-8 w-8 text-amber-500" />
              <h2 className="text-2xl font-bold text-amber-400">
                Registration Requirements
              </h2>
            </div>

            <Card className="border border-amber-500/20 bg-black/60 shadow-lg backdrop-blur-sm">
              <CardContent className="p-6 space-y-6">
                <div className="flex items-start gap-4">
                  <User className="h-6 w-6 mt-0.5 text-amber-500" />
                  <div>
                    <p className="font-bold text-lg text-white">
                      Profile Setup Required
                    </p>
                    <p className="text-zinc-400">
                      To create articles or post in forums, you must complete
                      your profile by adding a username and bio. This helps
                      maintain quality content and community trust.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <PenSquare className="h-6 w-6 mt-0.5 text-amber-500" />
                  <div>
                    <p className="font-bold text-lg text-white">
                      Content Creation
                    </p>
                    <p className="text-zinc-400">
                      Once your profile is complete, you can write and publish
                      articles, share your thoughts, and contribute to our
                      knowledge base.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <MessageSquare className="h-6 w-6 mt-0.5 text-amber-500" />
                  <div>
                    <p className="font-bold text-lg text-white">
                      Forum Participation
                    </p>
                    <p className="text-zinc-400">
                      Join discussions, ask questions, and share insights with
                      other community members after completing your profile
                      setup.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Security Section */}
      <section className="py-16 bg-black">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 rounded-full bg-green-500/10 flex items-center justify-center border border-green-500/20">
                  <Shield className="h-5 w-5 text-green-500" />
                </div>
                <h2 className="text-2xl font-bold text-white">
                  Secure & Private
                </h2>
              </div>

              <div className="space-y-4">
                <p className="text-zinc-400">
                  Your wallet is your identity. We don't collect personal data
                  or track your activities beyond what's necessary for the
                  platform to function.
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <p className="text-sm text-zinc-300">No email required</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <p className="text-sm text-zinc-300">
                      No password to remember
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <p className="text-sm text-zinc-300">
                      Full control of your identity
                    </p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
                    <p className="text-sm text-zinc-300">
                      Blockchain-verified actions
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <Card className="border border-white/10 overflow-hidden bg-zinc-900/50">
                <CardHeader className="bg-gradient-to-r from-green-900/20 to-emerald-900/20 pb-4 border-b border-white/5">
                  <CardTitle className="flex items-center gap-2 text-white">
                    <Zap className="h-5 w-5 text-green-500" />
                    Instant Access
                  </CardTitle>
                  <CardDescription className="text-zinc-400">
                    Connect once and access all features immediately
                  </CardDescription>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="space-y-4">
                    <p className="text-zinc-400">
                      With our wallet-based authentication system, you can:
                    </p>
                    <ul className="space-y-2 text-zinc-300">
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-green-500 mt-1" />
                        <span>Access your account from any device</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-green-500 mt-1" />
                        <span>No waiting for email verification</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-green-500 mt-1" />
                        <span>
                          Seamlessly interact with blockchain features
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <ArrowRight className="h-4 w-4 text-green-500 mt-1" />
                        <span>Maintain a consistent identity across Web3</span>
                      </li>
                    </ul>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
}
